import React, {
  useCallback,
  useState,
  useMemo,
} from 'react';
import format from 'string-template';
import PropTypes from 'prop-types';
import moment from 'moment';

import { formatCurrency } from '../../../../../utils/formatters';
import { DateFormat } from '../../../../../utils/date';
import GenericErrorMessage from '../../../../../components/GenericErrorMessage';
import colors from '../../../../../styles/colors';
import SubmitButton from '../SubmitButton';

import {
  Container,
  CheckoutContent,
  ErrorMessage,
  StripeCardNumberElement,
  StripeCardExpiryElement,
  StripeCardCvcElement,
  InputFieldGroup,
  ElementContainer,
  Label,
  BottomTextContainer,
  StyledLockIcon,
  DoubleElementContainer,
  PaymentSummary,
  PaymentSummaryTitle,
  PaymentSummaryContent,
  PaymentContentContainer,
  PaymentSummarySubContent,
} from './styles';

import texts from './texts.json';

const PaymentForm = ({
  isLoading,
  errorMessage,
  onSubmit,
  isDarkTheme,
  color,
  contract,
  isApplePayView,
}) => {
  // Stripe field errors
  const [fieldErrors, setFieldErrors] = useState({
    cardNumber: '',
    cardExpiry: '',
    cardCvc: '',
  });
  const [isFieldEmpty, setIsFieldEmpty] = useState({
    cardNumber: true,
    cardExpiry: true,
    cardCvc: true,
  });

  const {
    initialPayment,
    upfrontPayment,
    currency,
    startDate,
    initialTerm,
  } = contract;

  const submitButtonText = useMemo(() => {
    if (moment(startDate, DateFormat.DEFAULT_DATE_FORMAT).isAfter(moment()) && Number(upfrontPayment) === 0) {
      return texts.checkout;
    }
    return format(texts.payAmount, {
      amount: formatCurrency(upfrontPayment || initialPayment, currency),
    });
  }, [
    startDate,
    upfrontPayment,
    initialPayment,
    currency,
  ]);

  const stripeElementStyle = {
    base: {
      iconColor: isDarkTheme ? colors.base.alpha : colors.base.beta,
      color: isDarkTheme ? colors.base.alpha : colors.base.beta,
    },
  };

  const handleStripeFieldChange = useCallback((event) => {
    const { error, elementType, empty } = event;
    if (error) {
      setFieldErrors((prevErrors) => ({ ...prevErrors, [elementType]: error.message }));
    } else {
      setFieldErrors((prevErrors) => ({ ...prevErrors, [elementType]: '' }));
    }
    setIsFieldEmpty((prevState) => ({ ...prevState, [elementType]: empty }));
  }, []);

  const areFieldErrors = useMemo(() => (
    Object.values(fieldErrors).some((field) => !!field)
  ), [fieldErrors]);

  return (
    <Container>
      <CheckoutContent>
        {!isApplePayView && (
          <>
            <InputFieldGroup>
              <ElementContainer $isFieldEmpty={isFieldEmpty.cardNumber} $isDarkTheme={isDarkTheme}>
                <Label $isDarkTheme={isDarkTheme}>{texts.cardNumber}</Label>
                <StripeCardNumberElement
                  options={{
                    showIcon: true,
                    style: stripeElementStyle,
                  }}
                  onChange={handleStripeFieldChange}
                  $isDarkTheme={isDarkTheme}
                />
              </ElementContainer>
              <DoubleElementContainer>
                <ElementContainer
                  $isFieldEmpty={isFieldEmpty.cardExpiry}
                  $isDarkTheme={isDarkTheme}
                >
                  <Label $isDarkTheme={isDarkTheme}>{texts.expiryDate}</Label>
                  <StripeCardExpiryElement
                    onChange={handleStripeFieldChange}
                    $isDarkTheme={isDarkTheme}
                    options={{
                      style: stripeElementStyle,
                    }}
                  />
                </ElementContainer>
                <ElementContainer
                  $isFieldEmpty={isFieldEmpty.cardCvc}
                  $isDarkTheme={isDarkTheme}
                >
                  <Label $isDarkTheme={isDarkTheme}>{texts.cvc}</Label>
                  <StripeCardCvcElement
                    options={{
                      style: stripeElementStyle,
                    }}
                    onChange={handleStripeFieldChange}
                    $isDarkTheme={isDarkTheme}
                  />
                </ElementContainer>
              </DoubleElementContainer>
            </InputFieldGroup>
            {fieldErrors.cardNumber && <ErrorMessage>{fieldErrors.cardNumber}</ErrorMessage>}
            {fieldErrors.cardExpiry && <ErrorMessage>{fieldErrors.cardExpiry}</ErrorMessage>}
            {fieldErrors.cardCvc && <ErrorMessage>{fieldErrors.cardCvc}</ErrorMessage>}
          </>
        )}
        <PaymentSummary>
          <PaymentSummaryTitle $isDarkTheme={isDarkTheme}>{texts.paymentSummary}</PaymentSummaryTitle>
          {upfrontPayment > 0 && (
            <PaymentContentContainer>
              <PaymentSummaryContent $isDarkTheme={isDarkTheme}>
                <div>{texts.advancePayment}</div>
                <div>{formatCurrency(upfrontPayment, currency)}</div>
              </PaymentSummaryContent>
              <PaymentSummarySubContent $color={colors.base.danger}>
                {texts.dueToday}
              </PaymentSummarySubContent>
            </PaymentContentContainer>
          )}
          <PaymentContentContainer>
            <PaymentSummaryContent $isDarkTheme={isDarkTheme}>
              <div>
                {format(texts.firstPayment, {
                  ...(upfrontPayment > 0 && { balancePayment: texts.balanceOf }),
                  ...(initialTerm > 1 && { count: initialTerm }),
                })}
              </div>
              <div>
                {formatCurrency(
                  upfrontPayment ? initialPayment - upfrontPayment : initialPayment,
                  currency,
                )}
              </div>
            </PaymentSummaryContent>
            <PaymentSummarySubContent $color={upfrontPayment ? colors.shades.secondary11 : colors.shades.gamma14}>
              {upfrontPayment ? format(texts.billedOnDate, {
                startDate: moment(startDate).format(DateFormat.SHORT_MONTH_DAY_COMMA_YEAR_FORMAT),
              }) : format(texts.paymentPeriod, {
                startDate: moment(startDate).format(DateFormat.SHORT_MONTH_DAY_COMMA_YEAR_FORMAT),
                endDate: moment(startDate).add(
                  contract.initialTerm, 'months',
                ).format(DateFormat.SHORT_MONTH_DAY_COMMA_YEAR_FORMAT),
              })}
            </PaymentSummarySubContent>
          </PaymentContentContainer>
        </PaymentSummary>
        {!isApplePayView && (
          <>
            <SubmitButton
              isLoading={isLoading}
              disabled={areFieldErrors}
              onClick={onSubmit}
              $isDarkTheme={isDarkTheme}
              $background={color}
            >
              {submitButtonText}
            </SubmitButton>
            <BottomTextContainer $isDarkTheme={isDarkTheme}>
              <StyledLockIcon />
              {texts.endToEndEncrypted}
            </BottomTextContainer>
            {
              errorMessage && <GenericErrorMessage>{errorMessage}</GenericErrorMessage>
            }
          </>
        )}
      </CheckoutContent>
    </Container>
  );
};

PaymentForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  color: PropTypes.string,
  errorMessage: PropTypes.string,
  isDarkTheme: PropTypes.bool.isRequired,
  contract: PropTypes.object.isRequired,
  isApplePayView: PropTypes.bool.isRequired,
};

PaymentForm.defaultProps = {
  errorMessage: null,
  color: '',
};

export default PaymentForm;
